import { Link } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Typography } from '@glplus/glpweb-dsl';
import { Panel } from '../components/Panel';
import type { Me, MyApplication } from '../lib/runnerApiClient';
import { INSURANCE_OPEN_STATUSES, METRO_LABEL } from '../lib/onboarding';
import { formatCardDate } from '../lib/insurance';
import { ORANGE_LINK_STYLE } from '../styles/colors';
import { ProfileLanguages } from '../components/ProfileLanguages';
import { RunnerPageHeading } from '../components/RunnerPageHeading';
import { RunnerAction } from '../components/RunnerAction';
import { useNavigate } from 'react-router';
import { PhonePanel } from '../components/PhonePanel';


/** Deck §9 (first slice): who you are to GL Plus Dispatch. Read-only here; an
 *  applicant edits the mutable fields on /application (spec §5 stage 6). */
export default function Account({ me, application, reload }: { me: Me; application: MyApplication | null; reload: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const es = i18n.language.startsWith('es'); const navigate = useNavigate();
  const showInsurance = me.lifecycle !== 'applicant' || (application ? INSURANCE_OPEN_STATUSES.includes(application.status) : false);
  return (
    <Box data-testid="page-account">
      <RunnerPageHeading title={es ? 'Información del perfil' : 'Profile Info'} subtitle={es ? 'Mantén tu información actualizada' : 'Keep your information current'} topic="account" onRefresh={() => void reload()} />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2.5, alignItems: 'start' }}>
        <Panel title={es ? 'Foto del perfil' : 'Profile photo'}><RunnerAction topic="account" buttonText={es ? 'Ver foto y estado de revisión' : 'View photo & review status'} ariaLabel={es ? 'Ver foto y estado de revisión' : 'View photo & review status'} onClick={() => navigate('/photo')} /></Panel>
        <Panel title={t('account.runnerAccount')}>
          <Row label={t('account.email')} value={me.email} testId="account-email" />
          <Row label={t('account.status')} value={t(`lifecycle.${me.lifecycle}`, { defaultValue: me.lifecycle })} />
          <Row label={t('account.runnerId')} value={me.glpdId ?? t('account.idPending')} testId="account-runner-id" />
          <Row label={t('account.displayName')} value={me.displayName ?? (application ? `${application.firstName} ${application.lastName.charAt(0)}.` : '—')} />
        </Panel>
        <PhonePanel me={me} onSaved={reload} />
        <Panel title={es ? 'Dirección' : 'Address'}><Typography>{es ? 'Solicita a soporte una actualización de la dirección de tu cuenta y sus datos de verificación.' : 'Request an account address and verification-record update with support.'}</Typography><Box sx={{ mt: 2 }}><RunnerAction topic="account" buttonText={es ? 'Contactar a soporte' : 'Contact support'} ariaLabel={es ? 'Contactar a soporte' : 'Contact support'} onClick={() => navigate('/help')} /></Box></Panel>
        <ProfileLanguages me={me} />
        {showInsurance && (
          <Panel title={t('insurance.accountTitle')} dataTestId="account-insurance">
            <Typography data-testid="account-insurance-status" sx={{ color: '#4b5563', fontSize: '14px' }}>
              {me.insuranceExpiresOn && !me.insuranceRequired ? t('insurance.accountExpires', { date: formatCardDate(me.insuranceExpiresOn, i18n.language) }) : t('insurance.accountNone')}
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <Link to="/insurance" data-testid="account-insurance-link" style={ORANGE_LINK_STYLE}>{t('insurance.accountLink')}</Link>
            </Box>
          </Panel>
        )}
        {application && (
          <Panel title={t('account.toldUs')}>
            <Row label={t('account.name')} value={`${application.firstName} ${application.lastName}`} />
            <Row label={t('account.phone')} value={application.phoneE164 ?? '—'} />
            <Row label={t('account.metro')} value={METRO_LABEL[application.metro] ?? application.metro} />
            <Row label={t('account.zones')} value={application.zonesInterested.join(', ') || '—'} />
            <Row label={t('account.transport')} value={t(`transport.${application.transportMode}`, { defaultValue: application.transportMode })} />
            {/* 🛑 This said "self-service profile editing arrives with the next
                portal release". It shipped — EditApplicationDetails covers the
                exact three fields below (phone, zones, transport) — but it
                renders on /application, which is only in the nav while you are
                an applicant. So the old line was false for applicants, who can
                edit today, and true-for-the-wrong-reason for active Runners,
                who cannot reach it. Name, email and DOB stay support-only in
                both cases: they are the identity the Checkr candidate is built
                from (§5a) and the API refuses them. */}
            {me.lifecycle === 'applicant' ? (
              <Typography sx={{ color: '#5f6b7a', fontSize: '12px', mt: 1.5 }}><Trans i18nKey="account.applicantNote" components={{ cta: <Link to="/application" style={ORANGE_LINK_STYLE} /> }} /></Typography>
            ) : (
              <Typography sx={{ color: '#5f6b7a', fontSize: '12px', mt: 1.5 }}>{t('account.runnerNote')}</Typography>
            )}
          </Panel>
        )}
      </Box>
    </Box>
  );
}
function Row({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 1, py: 0.75, borderBottom: '1px solid #f1f5f9' }}>
      <Typography sx={{ color: '#5f6b7a', fontSize: '13px' }}>{label}</Typography>
      <Typography data-testid={testId} sx={{ color: '#1f2937', fontSize: '14px', fontWeight: 600, wordBreak: 'break-word' }}>{value}</Typography>
    </Box>
  );
}
